import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";

import { ListPage } from "../components/list-page";
import { LogoMark } from "../components/logo";
import { PageLoader } from "../components/ui/spinner";
import { ApiError } from "../lib/api";
import { listPreviewQuery } from "../lib/queries";
import { requireSession } from "../lib/session";

/**
 * The guests' page, for the list's owners. Outside the `_authed` layout on purpose: the
 * point is to see the page exactly as family opens it, without the app header.
 */
export const Route = createFileRoute("/mes-listes/$listId/preview")({
  beforeLoad: ({ context, location }) => requireSession(context.queryClient, location.href),
  loader: ({ context: { queryClient }, params: { listId } }) =>
    queryClient.prefetchQuery(listPreviewQuery(listId)),
  component: ListPreviewPage,
});

function ListPreviewPage() {
  const { listId } = Route.useParams();
  const preview = useQuery(listPreviewQuery(listId));

  useEffect(() => {
    if (preview.data) document.title = `Aperçu · ${preview.data.list.headline} · Listou`;
  }, [preview.data]);

  if (preview.isPending) return <PageLoader />;

  if (preview.isError) {
    const missing = preview.error instanceof ApiError && preview.error.status === 404;
    return (
      <main className="mx-auto grid max-w-md justify-items-start gap-4 px-4 py-24">
        <LogoMark className="size-12" />
        <h1 className="text-4xl font-semibold">
          {missing ? "Cette liste n'est plus accessible" : "L'aperçu n'a pas pu s'ouvrir"}
        </h1>
        <p className="text-lg text-muted">
          {missing
            ? "Elle a été supprimée, ou vous n'en faites plus partie."
            : "Vérifiez votre connexion et réessayez."}
        </p>
        {missing ? (
          <Link to="/mes-listes" className="btn">
            Retour à mes listes
          </Link>
        ) : (
          <button type="button" className="btn" onClick={() => void preview.refetch()}>
            Réessayer
          </button>
        )}
      </main>
    );
  }

  return (
    <>
      <div className="sticky top-0 z-50 border-b-2 border-line bg-surface">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <p className="text-muted">
            <span className="font-bold text-ink">Aperçu.</span> C'est la page que voient vos
            proches.
          </p>
          <Link to="/mes-listes/$listId" params={{ listId }} className="btn-secondary">
            Modifier
          </Link>
        </div>
      </div>
      <ListPage list={preview.data.list} wishes={preview.data.wishes} preview />
    </>
  );
}
